import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Player, Team } from '@core/types';
import { balanceTeams } from '@core/utils/balancer';
import { formatTeamLevel } from '../../levels';
import { bibColor } from '../../components/ui';

// O balanceamento acontecendo, e não um print dele.
//
// Roda no navegador de quem visita, com o mesmo core que o servidor usa: dez
// jogadores com nível, e dois times que saem com o nível médio praticamente
// igual. É a promessa da dobra demonstrada em vez de repetida.
//
// É também a única forma de ver o balanceamento sem conta quando o nível pago
// entrar em vigor (§7 do ARQUITETURA-WEB.md).

export const DEMO_PLAYERS: Player[] = [
  { id: '1', name: 'Ana', level: 4.5 },
  { id: '2', name: 'Bruno', level: 2 },
  { id: '3', name: 'Carla', level: 3.5 },
  { id: '4', name: 'Diego', level: 5 },
  { id: '5', name: 'Elis', level: 1.5 },
  { id: '6', name: 'Fábio', level: 3 },
  { id: '7', name: 'Gabi', level: 4 },
  { id: '8', name: 'Hugo', level: 2.5 },
  { id: '9', name: 'Iris', level: 3.5 },
  { id: '10', name: 'João', level: 2 },
];

export default function BalancedDrawDemo() {
  const { t, i18n } = useTranslation();

  const [teams, setTeams] = useState<Team[] | null>(null);
  const [revealed, setRevealed] = useState(0);

  // Os tempos da revelação vivem em `ref` para poderem ser cancelados: sair da
  // página no meio da sequência não pode deixar timer mexendo em componente
  // desmontado.
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  const reducedMotion =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  function run() {
    timers.current.forEach(clearTimeout);
    timers.current = [];

    const next = balanceTeams(DEMO_PLAYERS, 2, 5);
    setTeams(next);
    setRevealed(reducedMotion ? next.length : 0);

    if (reducedMotion) return;

    // Um time por vez, como na sala ao vivo. A pausa é o que deixa dar para
    // comparar os dois níveis médios em vez de só ver dois blocos surgirem.
    next.forEach((_, index) => {
      timers.current.push(setTimeout(() => setRevealed(index + 1), 500 + index * 700));
    });
  }

  return (
    <div className="flex flex-col gap-4 rounded-card border border-chalk-200 bg-white p-4 sm:p-5 dark:border-pitch-700 dark:bg-pitch-900">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="eyebrow text-chalk-500 dark:text-chalk-400">{t('landing.demoLabel')}</span>

        <button
          type="button"
          onClick={run}
          className="rounded-lg bg-pitch-950 px-4 py-2 text-sm font-semibold text-chalk-50 transition-transform active:scale-[0.98] dark:bg-chalk-100 dark:text-pitch-950"
        >
          {teams === null ? t('landing.demoDraw') : t('landing.demoAgain')}
        </button>
      </div>

      {/* O elenco fica à vista o tempo todo: é a entrada, e sem ela os times
          seriam dois blocos de nomes sem nada a provar. */}
      <ul className="flex flex-wrap gap-1.5">
        {DEMO_PLAYERS.map((player) => (
          <li
            key={player.id}
            className="rounded-lg bg-chalk-100 px-2 py-1 text-xs dark:bg-pitch-800"
          >
            {player.name}{' '}
            <span className="text-chalk-500 tabular-nums dark:text-chalk-400">{player.level}</span>
          </li>
        ))}
      </ul>

      {teams === null ? (
        <p className="text-sm text-chalk-600 dark:text-chalk-400">{t('landing.demoIdle')}</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {teams.map((team, index) =>
            index < revealed ? (
              <div
                key={team.id}
                className="animate-bib-in overflow-hidden rounded-card border border-chalk-200 dark:border-pitch-700"
              >
                <div
                  className="h-1.5 w-full"
                  style={{ backgroundColor: bibColor(index) }}
                  aria-hidden
                />
                <div className="flex flex-col gap-2 p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs font-semibold">{team.name}</span>
                    <span className="text-xs text-chalk-500 tabular-nums dark:text-chalk-400">
                      {t('landing.demoAverage', {
                        value: formatTeamLevel(
                          team.totalStars,
                          team.players.length,
                          i18n.language,
                        ),
                      })}
                    </span>
                  </div>

                  <ul className="flex flex-col gap-0.5 text-sm">
                    {team.players.map((player) => (
                      <li key={player.id} className="flex justify-between gap-2">
                        <span>{player.name}</span>
                        <span className="text-chalk-500 tabular-nums dark:text-chalk-400">
                          {player.level}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div
                key={team.id}
                className="flex min-h-28 items-center justify-center rounded-card border border-dashed border-chalk-300 text-xs text-chalk-500 dark:border-pitch-700"
              >
                {t('landing.demoDrawing')}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}
